import { useEffect, useMemo, useState } from "react";
import { api, formatMoney, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export function BulkAssignModal({ accounts, onClose, onSaved }) {
  const { user } = useAuth();
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set()); // "date:index"
  const [targetAccount, setTargetAccount] = useState(accounts[0]?.id || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/journal/unassigned-trades");
        setTrades(data.trades || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of trades) {
      if (!map.has(t.date)) map.set(t.date, []);
      map.get(t.date).push(t);
    }
    return [...map.entries()]; // [[date, trades[]]]
  }, [trades]);

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const toggleAllForDate = (date, entries) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const keys = entries.map((t) => `${date}:${t.index}`);
      const allSelected = keys.every((k) => next.has(k));
      if (allSelected) keys.forEach((k) => next.delete(k));
      else keys.forEach((k) => next.add(k));
      return next;
    });
  };
  const selectAll = () => {
    setSelected(new Set(trades.map((t) => `${t.date}:${t.index}`)));
  };
  const clearAll = () => setSelected(new Set());

  const assign = async () => {
    if (!targetAccount) { setError("Pick an account."); return; }
    if (selected.size === 0) { setError("Select at least one trade."); return; }
    setError(null);
    setSaving(true);
    try {
      // Group selected by date
      const byDate = new Map();
      for (const key of selected) {
        const [date, idx] = key.split(":");
        if (!byDate.has(date)) byDate.set(date, []);
        byDate.get(date).push(parseInt(idx, 10));
      }
      const payload = {
        account_id: targetAccount,
        assignments: [...byDate.entries()].map(([date, trade_indices]) => ({ date, trade_indices })),
      };
      const { data } = await api.post("/journal/bulk-assign", payload);
      onSaved?.(data.updated_trades);
      onClose?.();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 md:p-8 overflow-y-auto bg-[#0F0E0C]/80 backdrop-blur-sm fade-in"
      onClick={onClose}
    >
      <div
        data-testid="bulk-assign-modal"
        className="relative w-full max-w-2xl bg-[#1A1815] border border-[#2E2A25] rounded-sm slide-up my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-[#1A1815]/95 backdrop-blur border-b border-[#2E2A25] px-6 py-4 flex items-start justify-between gap-3">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CFA66A]">— Bulk assign</p>
            <h2 className="font-head text-xl text-[#EBE5D9] mt-1">Tag unassigned trades</h2>
            <p className="text-xs text-[#A39A8E] mt-1">
              {loading ? "Loading…" : `${trades.length} unassigned trade${trades.length === 1 ? "" : "s"} in your journal`}
            </p>
          </div>
          <button
            data-testid="bulk-assign-close"
            onClick={onClose}
            className="text-[#A39A8E] hover:text-[#EBE5D9] w-8 h-8 flex items-center justify-center border border-[#2E2A25] hover:border-[#CFA66A] rounded-sm"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="font-mono text-[10px] uppercase tracking-widest text-[#A39A8E]">Assign to:</label>
            <select
              data-testid="bulk-assign-account"
              value={targetAccount}
              onChange={(e) => setTargetAccount(e.target.value)}
              className="bg-[#0F0E0C] border border-[#2E2A25] text-[#EBE5D9] px-3 py-1.5 text-sm font-mono rounded-sm focus:outline-none focus:border-[#CFA66A]"
            >
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
            <span className="flex-1" />
            <button data-testid="bulk-select-all" onClick={selectAll} className="font-mono text-[10px] uppercase tracking-widest text-[#CFA66A] hover:text-[#EBE5D9]">Select all</button>
            <button data-testid="bulk-select-none" onClick={clearAll} className="font-mono text-[10px] uppercase tracking-widest text-[#A39A8E] hover:text-[#EBE5D9]">Clear</button>
          </div>

          {loading ? (
            <p className="font-mono text-xs text-[#A39A8E]">Loading trades…</p>
          ) : trades.length === 0 ? (
            <p className="text-sm text-[#A39A8E] italic border border-dashed border-[#2E2A25] p-6 text-center rounded-sm">
              Every trade is already assigned — nothing to do here.
            </p>
          ) : (
            <div className="max-h-[400px] overflow-y-auto space-y-3 pr-1">
              {grouped.map(([date, dayTrades]) => {
                const keys = dayTrades.map((t) => `${date}:${t.index}`);
                const allChecked = keys.every((k) => selected.has(k));
                const someChecked = keys.some((k) => selected.has(k));
                return (
                  <div key={date} className="border border-[#2E2A25] bg-[#0F0E0C] p-3 rounded-sm">
                    <button
                      data-testid={`bulk-toggle-day-${date}`}
                      onClick={() => toggleAllForDate(date, dayTrades)}
                      className="flex items-center gap-2 mb-2"
                    >
                      <span className={`w-3 h-3 border rounded-[2px] ${
                        allChecked ? "bg-[#CFA66A] border-[#CFA66A]" : someChecked ? "bg-[#CFA66A]/30 border-[#CFA66A]" : "border-[#2E2A25]"
                      }`} />
                      <span className="font-mono text-xs text-[#EBE5D9]">{date}</span>
                    </button>
                    <div className="space-y-1 pl-5">
                      {dayTrades.map((t) => {
                        const key = `${date}:${t.index}`;
                        const checked = selected.has(key);
                        return (
                          <label key={key} data-testid={`bulk-trade-${key}`} className="flex items-center gap-2 text-xs font-mono cursor-pointer group">
                            <input type="checkbox" checked={checked} onChange={() => toggle(key)} className="accent-[#CFA66A]" />
                            <span className="text-[#EBE5D9]">{t.pair} · {(t.direction || "").toUpperCase()}</span>
                            <span className={`${t.pnl > 0 ? "text-[#7A9E80]" : t.pnl < 0 ? "text-[#B56258]" : "text-[#A39A8E]"}`}>
                              {formatMoney(t.pnl, user?.currency)}
                            </span>
                            {t.strategy && <span className="text-[#A39A8E]">· {t.strategy}</span>}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {error && <p className="font-mono text-xs text-[#B56258]">{error}</p>}
        </div>

        <div className="sticky bottom-0 bg-[#1A1815]/95 backdrop-blur border-t border-[#2E2A25] px-6 py-3 flex items-center justify-between gap-2">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#A39A8E]">
            {selected.size} selected
          </span>
          <div className="flex gap-2">
            <button onClick={onClose} className="bg-transparent border border-[#2E2A25] text-[#EBE5D9] hover:bg-[#26231F] px-4 py-2 text-sm rounded-sm font-mono uppercase tracking-widest text-xs">
              Cancel
            </button>
            <button
              data-testid="bulk-assign-save"
              onClick={assign}
              disabled={saving || selected.size === 0}
              className="bg-[#CFA66A] text-[#0F0E0C] font-medium px-5 py-2 text-sm rounded-sm hover:bg-[#EBE5D9] transition-colors disabled:opacity-50 font-mono uppercase tracking-widest"
            >
              {saving ? "Assigning…" : `Assign ${selected.size}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
